// Vizanya own cart drawer (v3, 2026-10-22), replaces the Kaching cart.
// Renders from /cart.js: tier bar (2 = 15%, 3 = 20%, 4+ = 25%), lines with color/size selects, savings, totals.
// Keeps the free winter set in sync: added at 2+ paid items, removed below 2 (the discounts themselves are Shopify automatic discounts).
class CartDrawerController {
  constructor() {
    this.drawer = document.querySelector('[data-cart-drawer]');
    if (!this.drawer) return;

    this.panel = this.drawer.querySelector('.cart-drawer__panel');
    this.body = this.drawer.querySelector('[data-cart-drawer-body]');
    this.footer = this.drawer.querySelector('[data-cart-drawer-footer]');
    this.tiersNode = this.drawer.querySelector('[data-vz-tiers]');
    this.liveRegion = this.drawer.querySelector('[data-cart-drawer-live-region]');
    this.countNodes = [...document.querySelectorAll('[data-cart-count]')];
    this.toggleNodes = [...document.querySelectorAll('[data-cart-toggle]')];
    this.drawerCount = this.drawer.querySelector('[data-cart-drawer-count]');
    this.subtotalNode = this.drawer.querySelector('[data-cart-drawer-subtotal]');
    this.savingsRow = this.drawer.querySelector('[data-vz-savings-row]');
    this.savingsNode = this.drawer.querySelector('[data-vz-savings]');
    this.shipNode = this.drawer.querySelector('[data-vz-ship]');
    this.currency = this.drawer.dataset.cartCurrency || 'USD';
    this.giftVariant = Number(this.drawer.dataset.giftVariant || 0);
    this.emptyUrl = this.drawer.dataset.emptyUrl || '/collections/all';
    this.tiers = [[2, 15], [3, 20], [4, 25]];
    this.products = new Map();
    this.pendingItemKeys = new Set();
    this.isRefreshing = false;
    this.syncingGift = false;
    this.lastActiveElement = null;
    this.activeAddButton = null;
    this.activeAddLabel = null;
    this.activeAddLabelText = '';

    this.bindUI();
  }

  root() {
    return window.Shopify?.routes?.root || '/';
  }

  bindUI() {
    this.toggleNodes.forEach((trigger) => {
      trigger.setAttribute('aria-haspopup', 'dialog');
      trigger.setAttribute('aria-expanded', 'false');
      trigger.addEventListener('click', async (event) => {
        event.preventDefault();
        this.lastActiveElement = trigger;
        this.open();
        await this.refresh();
      });
    });

    this.drawer.querySelectorAll('[data-cart-drawer-close]').forEach((button) => {
      button.addEventListener('click', () => this.close());
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.drawer.classList.contains('is-open')) this.close();
      else if (event.key === 'Tab' && this.drawer.classList.contains('is-open')) this.trapFocus(event);
    });

    document.addEventListener('submit', (event) => this.handleAddToCart(event));
    this.drawer.addEventListener('click', (event) => this.handleDrawerClick(event));
    this.drawer.addEventListener('change', (event) => this.handleDrawerChange(event));
  }

  async handleAddToCart(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('[data-product-form]')) return;
    const submitter = event.submitter;
    if (!submitter || submitter.getAttribute('name') !== 'add') return;
    event.preventDefault();

    const formData = new FormData(form);
    this.activeAddButton = submitter;
    this.activeAddLabel = submitter.querySelector('[data-add-to-cart-label]');
    this.activeAddLabelText = this.activeAddLabel?.textContent || '';
    this.hideAddToCartError(form);

    try {
      this.setAddButtonState(true);
      const response = await fetch(`${this.root()}cart/add.js`, { method: 'POST', headers: { Accept: 'application/json' }, body: formData });
      if (!response.ok) {
        const data = await response.json().catch(() => null);
        throw new Error(data?.description || data?.message || '');
      }
      await response.json();
      this.open();
      await this.refresh();
    } catch (error) {
      console.error(error);
      this.showAddToCartError(form, error?.message);
    } finally {
      this.setAddButtonState(false);
    }
  }

  showAddToCartError(form, message) {
    const errorElement = form.querySelector('[data-add-to-cart-error]');
    if (!errorElement) {
      window.location.href = form.action || '/cart';
      return;
    }
    errorElement.textContent = message || errorElement.dataset.genericError || '';
    errorElement.hidden = false;
  }

  hideAddToCartError(form) {
    const errorElement = form.querySelector('[data-add-to-cart-error]');
    if (errorElement) {
      errorElement.hidden = true;
      errorElement.textContent = '';
    }
  }

  async handleDrawerClick(event) {
    const item = event.target.closest('[data-cart-item-key]');
    const quantityTrigger = event.target.closest('[data-cart-quantity-change]');
    if (quantityTrigger && item) {
      const input = item.querySelector('[data-cart-quantity-input]');
      const next = Math.max(0, Number(input?.value || 0) + Number(quantityTrigger.dataset.cartQuantityDelta || 0));
      await this.changeItem(item.dataset.cartItemKey, next);
      return;
    }
    if (event.target.closest('[data-cart-remove]') && item) {
      await this.changeItem(item.dataset.cartItemKey, 0);
    }
  }

  async handleDrawerChange(event) {
    const item = event.target.closest('[data-cart-item-key]');
    if (!item) return;

    if (event.target.matches('[data-cart-quantity-input]')) {
      await this.changeItem(item.dataset.cartItemKey, Math.max(0, Number(event.target.value) || 0));
      return;
    }

    // color or size changed: swap the line for the matching variant, same quantity
    if (event.target.matches('[data-vz-option]')) {
      const product = this.products.get(item.dataset.handle);
      if (!product) return;
      const chosen = [...item.querySelectorAll('[data-vz-option]')].map((select) => select.value);
      const variant = product.variants.find((v) => v.options.every((value, i) => value === chosen[i]));
      if (!variant || variant.id === Number(item.dataset.variantId)) return;
      if (!variant.available) {
        event.target.value = event.target.dataset.current;
        return;
      }
      await this.swapVariant(item.dataset.cartItemKey, variant.id, Number(item.dataset.quantity || 1));
    }
  }

  async changeItem(key, quantity) {
    if (!key || this.pendingItemKeys.has(key)) return;
    this.pendingItemKeys.add(key);
    this.setItemPendingState(key, true);
    try {
      const response = await fetch(`${this.root()}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity })
      });
      if (!response.ok) throw new Error('Unable to update cart item');
    } catch (error) {
      console.error(error);
    } finally {
      this.pendingItemKeys.delete(key);
      await this.refresh();
    }
  }

  async swapVariant(key, variantId, quantity) {
    if (this.pendingItemKeys.has(key)) return;
    this.pendingItemKeys.add(key);
    this.setItemPendingState(key, true);
    try {
      await fetch(`${this.root()}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: 0 })
      });
      await fetch(`${this.root()}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity }] })
      });
    } catch (error) {
      console.error(error);
    } finally {
      this.pendingItemKeys.delete(key);
      await this.refresh();
    }
  }

  async fetchCart() {
    const response = await fetch(`${this.root()}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
    return response.json();
  }

  // free winter set: exactly 1 at 2+ paid items, none below 2
  async syncGift(cart) {
    if (!this.giftVariant || this.syncingGift) return cart;
    const giftQty = cart.items.filter((i) => i.variant_id === this.giftVariant).reduce((s, i) => s + i.quantity, 0);
    const paidQty = (cart.item_count || 0) - giftQty;
    const wanted = paidQty >= 2 ? 1 : 0;
    if (giftQty === wanted) return cart;

    this.syncingGift = true;
    try {
      if (giftQty === 0) {
        await fetch(`${this.root()}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: this.giftVariant, quantity: 1, properties: { _gift: 'Clearance Sale' } }] })
        });
      } else {
        await fetch(`${this.root()}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates: { [this.giftVariant]: wanted } })
        });
      }
      document.dispatchEvent(new CustomEvent('cart:updated'));
      return await this.fetchCart();
    } catch (error) {
      console.error(error);
      return cart;
    } finally {
      this.syncingGift = false;
    }
  }

  async loadProducts(cart) {
    const handles = [...new Set(cart.items.map((i) => i.handle))].filter((h) => !this.products.has(h));
    await Promise.all(handles.map(async (handle) => {
      try {
        const response = await fetch(`${this.root()}products/${handle}.js`, { headers: { Accept: 'application/json' } });
        if (response.ok) this.products.set(handle, await response.json());
      } catch (error) {
        console.error(error);
      }
    }));
  }

  async refresh() {
    if (this.isRefreshing) return null;
    this.isRefreshing = true;
    if (!this.body.children.length) this.showSkeleton();
    try {
      let cart = await this.fetchCart();
      cart = await this.syncGift(cart);
      await this.loadProducts(cart);
      this.render(cart);
      return cart;
    } finally {
      this.isRefreshing = false;
    }
  }

  showSkeleton() {
    this.body.innerHTML = '<div class="vz-cart__loading" aria-busy="true">Loading your cart&hellip;</div>';
  }

  compareUnit(item) {
    const product = this.products.get(item.handle);
    const variant = product?.variants.find((v) => v.id === item.variant_id);
    return Math.max(Number(variant?.compare_at_price || 0), Number(item.original_price || 0));
  }

  renderTiers(paidQty, giftInCart) {
    if (!this.tiersNode) return;
    if (!paidQty) {
      this.tiersNode.hidden = true;
      this.tiersNode.innerHTML = '';
      return;
    }
    const next = this.tiers.find((t) => paidQty < t[0]);
    const current = [...this.tiers].reverse().find((t) => paidQty >= t[0]);
    let msg = next
      ? `Add <strong>${next[0] - paidQty}</strong> more item${next[0] - paidQty > 1 ? 's' : ''} to get <strong>${next[1]}% off</strong>${paidQty < 2 ? ' + a <strong>FREE winter set</strong>' : ''}`
      : 'You unlocked <strong>25% off</strong> your entire order!';
    if (giftInCart) msg = `&#127873; <strong>Free winter set added</strong> (worth $35). ${msg}`;
    if (current && next) msg = `<strong>${current[1]}% off</strong> applied. ${msg}`;
    const dots = [50, 75, 100]
      .map((left, k) => `<span class="vz-cart__dot${paidQty >= this.tiers[k][0] ? ' is-hit' : ''}" style="left:${left}%"></span>`)
      .join('');
    this.tiersNode.innerHTML = `<p class="vz-cart__tiers-msg">${msg}</p>
      <div class="vz-cart__track"><span class="vz-cart__fill" style="width:${Math.min((paidQty / 4) * 100, 100)}%"></span>${dots}</div>
      <div class="vz-cart__tier-labels"><span>2: 15% off</span><span>3: 20% off</span><span>4+: 25% off</span></div>`;
    this.tiersNode.hidden = false;
  }

  renderLine(item) {
    const isGift = item.variant_id === this.giftVariant;
    const product = this.products.get(item.handle);
    const compareLine = this.compareUnit(item) * item.quantity;
    const finalLine = Number(item.final_line_price || 0);
    const saved = Math.max(0, compareLine - finalLine);
    const pending = this.pendingItemKeys.has(item.key);
    const imageUrl = item.image ? `${item.image}${item.image.includes('?') ? '&' : '?'}width=200` : '';
    const image = imageUrl ? `<img src="${this.escapeHtml(imageUrl)}" alt="${this.escapeHtml(item.product_title)}" loading="lazy">` : '';

    let options = '';
    if (!isGift && product && product.variants.length > 1) {
      options = product.options
        .map((option, i) => {
          const name = typeof option === 'string' ? option : option.name;
          const values = typeof option === 'string' ? [...new Set(product.variants.map((v) => v.options[i]))] : option.values;
          const current = item.variant_options?.[i] ?? '';
          return `<label class="vz-cart__select"><span class="visually-hidden">${this.escapeHtml(name)}</span>
            <select data-vz-option data-current="${this.escapeHtml(current)}" ${pending ? 'disabled' : ''}>
              ${values.map((value) => `<option value="${this.escapeHtml(value)}"${value === current ? ' selected' : ''}>${this.escapeHtml(value)}</option>`).join('')}
            </select></label>`;
        })
        .join('');
    }

    const controls = isGift
      ? '<p class="vz-cart__gift-note">Free with 2 or more items</p>'
      : `<div class="vz-cart__controls">
          <div class="vz-cart__qty">
            <button type="button" data-cart-quantity-change data-cart-quantity-delta="-1" aria-label="Decrease quantity" ${pending ? 'disabled' : ''}>&minus;</button>
            <input type="number" min="0" value="${item.quantity}" aria-label="Quantity" data-cart-quantity-input ${pending ? 'disabled' : ''}>
            <button type="button" data-cart-quantity-change data-cart-quantity-delta="1" aria-label="Increase quantity" ${pending ? 'disabled' : ''}>+</button>
          </div>
          <button type="button" class="vz-cart__remove" data-cart-remove aria-label="Remove ${this.escapeHtml(item.product_title)}" ${pending ? 'disabled' : ''}>
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7h16M10 11v6M14 11v6M6 7l1 12a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-12M9 7V4h6v3"/></svg>
          </button>
        </div>`;

    return `<li class="vz-cart__line${pending ? ' is-pending' : ''}${isGift ? ' vz-cart__line--gift' : ''}" data-cart-item-key="${item.key}" data-handle="${this.escapeHtml(item.handle)}" data-variant-id="${item.variant_id}" data-quantity="${item.quantity}">
        <a href="${this.escapeHtml(item.url)}" class="vz-cart__media">${image}</a>
        <div class="vz-cart__info">
          <a href="${this.escapeHtml(item.url)}" class="vz-cart__name">${this.escapeHtml(item.product_title)}</a>
          ${options ? `<div class="vz-cart__options">${options}</div>` : ''}
          ${controls}
        </div>
        <div class="vz-cart__prices">
          ${compareLine > finalLine ? `<s>${this.formatMoney(compareLine)}</s>` : ''}
          <strong>${finalLine === 0 ? 'FREE' : this.formatMoney(finalLine)}</strong>
          ${saved > 0 ? `<span class="vz-cart__saved">You save ${this.formatMoney(saved)}</span>` : ''}
        </div>
      </li>`;
  }

  render(cart) {
    const itemCount = cart.item_count || 0;
    const giftQty = cart.items.filter((i) => i.variant_id === this.giftVariant).reduce((s, i) => s + i.quantity, 0);
    const paidQty = itemCount - giftQty;

    if (this.drawerCount) this.drawerCount.textContent = itemCount;
    this.countNodes.forEach((node) => {
      node.textContent = itemCount;
      node.hidden = itemCount === 0;
    });
    if (this.footer) this.footer.hidden = itemCount === 0;
    this.renderTiers(paidQty, giftQty > 0);

    if (!itemCount) {
      this.body.innerHTML = `<div class="vz-cart__empty">
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 8h12l-1 12H7L6 8zM9 8V6a3 3 0 0 1 6 0v2"/></svg>
          <p>Your cart is empty</p>
          <a href="${this.escapeHtml(this.emptyUrl)}" class="button button--primary">Shop the Clearance</a>
        </div>`;
      return;
    }

    // gift line last, paid items in cart order
    const lines = [...cart.items].sort((a, b) => (a.variant_id === this.giftVariant) - (b.variant_id === this.giftVariant));
    this.body.innerHTML = `<ul class="vz-cart__lines" role="list">${lines.map((item) => this.renderLine(item)).join('')}</ul>`;

    const savings = cart.items.reduce((sum, item) => sum + Math.max(0, this.compareUnit(item) * item.quantity - Number(item.final_line_price || 0)), 0);
    if (this.subtotalNode) this.subtotalNode.textContent = this.formatMoney(cart.total_price || 0);
    if (this.savingsNode) this.savingsNode.textContent = `-${this.formatMoney(savings)}`;
    if (this.savingsRow) this.savingsRow.hidden = savings <= 0;
    if (this.shipNode) this.shipNode.innerHTML = `Order today, <strong>${window.vz2Ship || 'ships tomorrow'}</strong>`;
    if (this.liveRegion) this.liveRegion.textContent = `${itemCount} items in cart, total ${this.formatMoney(cart.total_price || 0)}`;
  }

  setBackgroundInert(isInert) {
    if (isInert) {
      this.inertedElements = [...document.body.children].filter((el) => el !== this.drawer && el.tagName !== 'SCRIPT' && !el.hasAttribute('inert'));
      this.inertedElements.forEach((el) => el.setAttribute('inert', ''));
    } else {
      (this.inertedElements || []).forEach((el) => el.removeAttribute('inert'));
      this.inertedElements = [];
    }
  }

  open() {
    if (this.drawer.classList.contains('is-open')) return;
    this.drawer.classList.add('is-open');
    this.drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('cart-drawer-open');
    this.toggleNodes.forEach((node) => node.setAttribute('aria-expanded', 'true'));
    this.setBackgroundInert(true);
    this.panel.querySelector('#CartDrawerTitle')?.focus();
  }

  close() {
    this.drawer.classList.remove('is-open');
    this.drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('cart-drawer-open');
    this.toggleNodes.forEach((node) => node.setAttribute('aria-expanded', 'false'));
    this.setBackgroundInert(false);
    this.lastActiveElement?.focus();
  }

  setAddButtonState(isLoading) {
    if (!this.activeAddButton) return;
    this.activeAddButton.disabled = isLoading;
    if (this.activeAddLabel) this.activeAddLabel.textContent = isLoading ? 'Adding...' : this.activeAddLabelText;
  }

  setItemPendingState(key, isPending) {
    const item = this.body?.querySelector(`[data-cart-item-key="${CSS.escape(key)}"]`);
    if (!item) return;
    item.classList.toggle('is-pending', isPending);
    item.querySelectorAll('button, select, input').forEach((node) => { node.disabled = isPending; });
  }

  trapFocus(event) {
    const nodes = [...this.panel.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')].filter((n) => !n.hasAttribute('disabled'));
    if (!nodes.length) return;
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  formatMoney(cents) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: this.currency }).format((cents || 0) / 100);
  }

  escapeHtml(value) {
    return String(value ?? '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');
  }
}

document.addEventListener('DOMContentLoaded', () => {
  window.vzCart = new CartDrawerController();
});
